
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MdArrowBack, MdSave, MdEdit } from 'react-icons/md';
import './AddVendor.css';
import axios from 'axios';
import { UPDATE_VENDOR_API, VIEW_VENDOR_API } from '../config/API';
import Swal from 'sweetalert2';
import Loader from '../components/common/Loader';
import { showErrorToast } from '../utils/toastUtils';

const EditVendor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Detect if we are in 'view' mode (from path or state)
    const [isViewMode, setIsViewMode] = useState(
        location.pathname.includes('/view/') || location.state?.mode === 'view'
    );
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [formData, setFormData] = useState({
        companyName: '',
        name: '',
        whatsappNumber: '',
        address: '',
        category: '',
        contactPerson: '',
        gst: '',
        pan: ''
    });

    useEffect(() => {
        const fetchVendorData = async () => {
            try {
                setFetchingData(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.get(VIEW_VENDOR_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    const v = response.data.data;
                    setFormData({
                        companyName: v.companyName || '',
                        name: v.vendorName || '',
                        whatsappNumber: v.whatsAppNumber || '',
                        address: v.address || '',
                        category: v.category || '',
                        contactPerson: v.contactPerson || '',
                        gst: v.gstNumber || '',
                        pan: v.panNumber || ''
                    });
                }
            } catch (error) {
                console.error('Error fetching vendor data:', error);
            } finally {
                setFetchingData(false);
            }
        };

        fetchVendorData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.companyName) {
            showErrorToast('Company name is required');
            return;
        }

        if (isViewMode) return;

        const result = await Swal.fire({
            title: 'Update Vendor?',
            text: 'Do you want to save changes to this vendor?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.put(UPDATE_VENDOR_API(id), formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });


                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: 'Vendor updated successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/vendors');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to update vendor');
                }
            } catch (error) {
                console.error('Error updating vendor:', error);
                const errorData = error.response?.data?.error || error.response?.data?.message || 'An error occurred';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <Loader fullPage text="Updating vendor..." />;
    }

    return (
        <div className="add-vendor-page">
            <div className="page-header">
                <button onClick={() => navigate('/vendors')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{isViewMode ? 'Vendor Details' : 'Edit Vendor'}</h2>
                {isViewMode && (
                    <button onClick={() => setIsViewMode(false)} className="edit-toggle-btn" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        <MdEdit /> Edit Mode
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="vendor-form">
                <div className="form-section">
                    <h3>Vendor Details</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Company Name<span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                placeholder="Enter company name"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Vendor Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter vendor name"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Contact Person</label>
                            <input
                                type="text"
                                name="contactPerson"
                                value={formData.contactPerson}
                                onChange={handleChange}
                                placeholder="Enter contact person name"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>WhatsApp Number <span className="required-star">*</span></label>
                            <input
                                type="tel"
                                name="whatsappNumber"
                                value={formData.whatsappNumber}
                                onChange={handleChange}
                                placeholder="Enter WhatsApp number"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Address</label>
                            <input
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Enter full address"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Category</label>
                            <input
                                type="text"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                placeholder="e.g. Electronics, Stationery"
                                disabled={isViewMode}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Tax Information</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>GST Number</label>
                            <input
                                type="text"
                                name="gst"
                                value={formData.gst}
                                onChange={handleChange}
                                placeholder="Enter GST number"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>PAN Number</label>
                            <input
                                type="text"
                                name="pan"
                                value={formData.pan}
                                onChange={handleChange}
                                placeholder="Enter PAN number"
                                disabled={isViewMode}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/vendors')} className="cancel-btn">
                        {isViewMode ? 'Back to List' : 'Cancel'}
                    </button>
                    {!isViewMode && (
                        <button type="submit" className="save-btn">
                            <MdSave /> Update Vendor
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EditVendor;
